import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HomeIcon,
  FolderIcon,
  ClipboardListIcon,
  CubeIcon,
  UsersIcon,
  ChartBarIcon,
  CogIcon,
  XIcon,
  UserGroupIcon,
  ClockIcon,
  DocumentReportIcon,
} from "@heroicons/react/outline";
import { useAuth } from "../../context/AuthContext";

const Sidebar = ({ isOpen, isMobile, onClose }) => {
  const { user, hasAnyRole } = useAuth();
  const location = useLocation();

  const getNavItems = () => {
    const role = user?.role;

    const baseItems = [
      { path: "/dashboard", icon: HomeIcon, label: "Dashboard" },
    ];

    const roleItems = {
      super_admin: [
        { path: "/projects", icon: FolderIcon, label: "Projects" },
        { path: "/stages", icon: ClipboardListIcon, label: "Stages" },
        { path: "/tasks", icon: ClipboardListIcon, label: "Tasks" },
        { path: "/inventory", icon: CubeIcon, label: "Inventory" },
        { path: "/labor", icon: UsersIcon, label: "Labor" },
        { path: "/reports", icon: ChartBarIcon, label: "Reports" },
        { path: "/settings", icon: CogIcon, label: "Settings" },
      ],
      project_manager: [
        { path: "/projects", icon: FolderIcon, label: "My Projects" },
        { path: "/stages", icon: ClipboardListIcon, label: "Stages" },
        { path: "/tasks", icon: ClipboardListIcon, label: "Tasks" },
        { path: "/inventory", icon: CubeIcon, label: "Inventory" },
        { path: "/labor", icon: UsersIcon, label: "Labor" },
        { path: "/reports", icon: ChartBarIcon, label: "Reports" },
        { path: "/settings", icon: CogIcon, label: "Settings" },
      ],
      sub_manager: [
        { path: "/projects", icon: FolderIcon, label: "My Projects" },
        { path: "/stages", icon: ClipboardListIcon, label: "Stages" },
        { path: "/tasks", icon: ClipboardListIcon, label: "Tasks" },
        { path: "/inventory", icon: CubeIcon, label: "Inventory" },
        { path: "/labor", icon: UsersIcon, label: "Labor" },
        // Reports removed for Sub Manager
      ],
      foreman: [
        { path: "/projects", icon: FolderIcon, label: "My Projects" },
        { path: "/tasks", icon: ClipboardListIcon, label: "Tasks" },
        { path: "/labor", icon: UsersIcon, label: "Labor" },
      ],
      worker: [
        { path: "/tasks", icon: ClipboardListIcon, label: "My Tasks" },
        { path: "/labor", icon: ClockIcon, label: "Timesheet" },
      ],
    };

    const items = roleItems[role] || roleItems.worker;
    return [...baseItems, ...items];
  };

  const navItems = getNavItems();

  const sidebarContent = (
    <div className="flex flex-col h-full bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700">
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25">
            <span className="text-white font-bold text-lg">CM</span>
          </div>
          <div>
            <h1 className="font-bold text-slate-900 dark:text-white text-lg leading-tight">
              Construx
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-tight">
              {user?.role
                ? user.role.replace("_", " ").toUpperCase()
                : "Worker"}
            </p>
          </div>
        </div>
        {isMobile && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors lg:hidden"
          >
            <XIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {navItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path !== "/dashboard" &&
              location.pathname.startsWith(item.path));

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={isMobile ? onClose : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
                  isActive
                    ? "bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-semibold shadow-sm"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={`h-5 w-5 flex-shrink-0 ${
                      isActive
                        ? "text-primary-500"
                        : "text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300"
                    }`}
                  />
                  <span className="text-sm flex-1">{item.label}</span>
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="w-1.5 h-6 bg-primary-500 rounded-full"
                      transition={{ duration: 0.2 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User info with role badge */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50">
          <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {user?.full_name
              ?.split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {user?.full_name || "User"}
            </p>
            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-2 h-2 rounded-full ${user?.is_active ? "bg-green-500" : "bg-red-500"}`}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {user?.role
                  ? user.role.replace("_", " ").toUpperCase()
                  : "Worker"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Mobile sidebar
  if (isMobile) {
    return (
      <motion.div
        initial={{ x: -280, opacity: 0 }}
        animate={{ x: isOpen ? 0 : -280, opacity: isOpen ? 1 : 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className={`fixed top-0 left-0 z-50 h-full w-72 lg:hidden ${
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        style={{ boxShadow: isOpen ? "0 0 40px rgba(0,0,0,0.2)" : "none" }}
      >
        {sidebarContent}
      </motion.div>
    );
  }

  return (
    <aside className="fixed top-0 left-0 z-30 h-full w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 lg:block hidden">
      {sidebarContent}
    </aside>
  );
};

export default Sidebar;
